import React from 'react';
import Promise from 'bluebird';
import _ from 'lodash';
import { connect } from 'react-redux';
import FacebookBtn from 'components/facebookBtn';
import LoadingSpinner from 'scy/LoadingSpinner';
import { TextHR } from 'scy/HR';
import Select from 'scy/Select';
import {
  validEmail,
  validDate,
  underage,
  formatBirthday,
  displayBirthday
} from 'shared/models/user';
import FormField from 'scy/FormField';
import PhoneInput from 'shared/components/phoneInput';
import { LoadingButton } from 'scy/buttons';
import { DangerWarning } from 'scy/Warning';
import GuestExperiment from './guestExperiment';
import { trackNow } from 'shared/models/analytic';
import { signup, updateUser } from './actions';
import { textLeft, tablet_textRight } from 'scy/utils.mod.scss';
import { flex, flex1 } from 'scy/flex.mod.scss';
import margin from 'scy/margin.mod.scss';

const SOURCE_OPTIONS = [
  'Website / Blog',
  'Radio / Podcast',
  'Social Media',
  'Friends',
  'Search',
  'On a Train or Billboard'
];

let SignupForm = class SignupForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sourceOptions: null,
      loading: false,
      errors: {},
      error: null,
      fbLoading: false,
      source: '',
      isGuest: false
    };
    this.setModel(props.user);
  }

  componentDidMount() {
    const sourceOptions = _.shuffle(SOURCE_OPTIONS)
      .concat('Other')
      .map(option => ({ value: option, label: option }));
    this.setState({ sourceOptions });
  }

  setModel(params) {
    this.model = {
      firstname: params.firstname,
      lastname: params.lastname,
      birthday: params.birthday,
      email: params.email,
      mobile: params.mobile,
      password: params.password
    };
  }

  setFBLoading() {
    this.setState({ fbLoading: true });
  }

  setFBUser(user) {
    this.setModel(user.attributes);
    this.setState({ fbLoading: false });
  }

  handleFBSignup(user) {
    this.setFBUser(user);
    if (this.props.onFBSignup) {
      return this.validateFields()
        .then(() => this.props.onFBSignup(user))
        .catch(({ message }) => {
          this.setState({ loading: false, error: message });
        });
    }
  }

  handleFBLogin(user) {
    this.setFBUser(user);
    if (this.props.onFBLogin) {
      return this.props.onFBLogin(user).catch(({ message }) => {
        this.setState({ loading: false, error: message });
      });
    } else if (this.props.onSuccess) {
      return this.props.onSuccess(user);
    }
  }

  validateFirstname(name) {
    if (_.isEmpty(name)) return "First Name can't be blank";
  }

  validateLastname(name) {
    if (_.isEmpty(name)) return "Last Name can't be blank";
  }

  validateBirthday(birthday) {
    if (!validDate(birthday)) return 'Birthday is not valid (mm/dd/yyyy)';
    if (underage(birthday)) return 'You must be 21 or older';
  }

  validateEmail(email) {
    if (!validEmail(email)) return 'Email is not valid';
  }

  validatePassword(password) {
    if (!password) return "Password can't be blank";
    if (password.length < 6) return 'Password must be at least 6 characters';
  }

  validateFields() {
    const {
      firstname,
      lastname,
      email,
      mobile,
      birthday,
      password
    } = this.model;

    let errors = {};
    errors.firstname = this.validateFirstname(firstname) || null;
    errors.lastname = this.validateLastname(lastname) || null;
    errors.email = this.validateEmail(email) || null;
    if (this.validateMobile) {
      errors.mobile = this.validateMobile(mobile) || null;
    }
    errors.birthday = this.validateBirthday(birthday) || null;
    if (!this.props.user.objectId)
      errors.password = this.validatePassword(password) || null;

    this.setState({ errors });

    if (_.some(errors)) return Promise.reject(new Error());
    return Promise.resolve();
  }

  _submit() {
    const data = {
      firstname: this.model.firstname.trim(),
      lastname: this.model.lastname.trim(),
      email: this.model.email.trim(),
      mobile: this.model.mobile.trim(),
      birthday: formatBirthday(this.model.birthday)
    };

    if (this.state.source) data.stated_source = this.state.source.value;

    if (!this.props.user || !this.props.user.objectId) {
      data.password = this.model.password;
      return this._signup(data);
    } else {
      return this._update(data);
    }
  }

  _signup(user) {
    return this.props
      .signup(user)
      .then(user => {
        if (this.props.checkout) {
          trackNow('Checkout Sign Up', { guest: this.state.isGuest });
        }
        trackNow('Registration', {
          accountType: 'Email',
          promoCode: this.props.promoCode
        });
        return user;
      })
      .catch(({ message }) => {
        trackNow('Registration Failed', {
          accountType: 'Email',
          reason: typeof message === 'object' ? message.message : message
        });
        const error = new Error();
        error.message = message;
        return Promise.reject(error);
      });
  }

  _update(user) {
    return this.props.updateUser(user);
  }

  handleSubmit(e) {
    e.preventDefault();
    this.setState({ loading: true, error: null });
    return this.validateFields()
      .then(() => this._submit())
      .then(user => {
        this.setState({ loading: false, error: null });
        this.props.onSuccess && this.props.onSuccess(user);
        return user;
      })
      .catch(({ message }) => {
        if (typeof message === 'object' && typeof message.reason === 'object') {
          this.setState({ loading: false, errors: message.reason });
        } else {
          this.setState({ loading: false, error: message });
        }
      });
  }

  render() {
    const user = this.props.user;
    const compact = this.props.compact;
    const showPromo = !!this.props.onPromoChange;

    return (
      <div className={textLeft}>
        {!user.objectId && !this.state.fbLoading && (
          <>
            <div className={flex}>
              <FacebookBtn
                className={flex1}
                onLoading={() => this.setFBLoading()}
                onComplete={user => this.handleFBSignup(user)}
                onLogin={user => this.handleFBLogin(user)}
                promoCode={this.props.promoCode}
              />
            </div>
            <TextHR>Or</TextHR>
          </>
        )}

        {this.state.fbLoading && <LoadingSpinner backdrop green />}

        <form onSubmit={e => this.handleSubmit(e)}>
          {this.state.error && (
            <DangerWarning>{this.state.error}</DangerWarning>
          )}

          {this.props.checkout && (
            <GuestExperiment
              checked={this.state.isGuest}
              onChange={() =>
                this.setState(state => ({ isGuest: !state.isGuest }))
              }
            />
          )}

          <div className={`row ${margin.bottom_xs}`}>
            <div className={compact ? 'col-xs-6' : 'col-xs-12 col-md-6'}>
              <FormField
                id="user-first-name"
                name="fname"
                labelText="First Name"
                initialValue={user.firstname}
                updatedValue={user.firstname}
                onChange={firstname =>
                  (this.model = { ...this.model, firstname })
                }
                validate={firstname => this.validateFirstname(firstname)}
                error={this.state.errors.firstname}
              />
            </div>

            <div className={compact ? 'col-xs-6' : 'col-xs-12 col-md-6'}>
              <FormField
                id="user-last-name"
                name="lname"
                labelText="Last Name"
                initialValue={user.lastname}
                updatedValue={user.lastname}
                onChange={lastname =>
                  (this.model = { ...this.model, lastname })
                }
                validate={lastname => this.validateLastname(lastname)}
                error={this.state.errors.lastname}
              />
            </div>

            <div className="col-xs-12">
              <FormField
                id="user-birthday"
                name="birthday"
                labelText="Birthday"
                placeholder="mm/dd/yyyy"
                mask={[
                  /\d/,
                  /\d/,
                  '/',
                  /\d/,
                  /\d/,
                  '/',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/
                ]}
                inputType="tel"
                initialValue={displayBirthday(user.birthday)}
                updatedValue={displayBirthday(user.birthday)}
                onChange={birthday =>
                  (this.model = { ...this.model, birthday })
                }
                validate={birthday => this.validateBirthday(birthday)}
                error={this.state.errors.birthday}
              />
            </div>

            <div className="col-xs-12">
              <FormField
                id="user-email"
                name="email"
                labelText="Email"
                inputType="email"
                autocomplete="username email"
                initialValue={user.email}
                updatedValue={user.email}
                onChange={email => (this.model = { ...this.model, email })}
                validate={email => this.validateEmail(email)}
                error={this.state.errors.email}
              />
            </div>

            <div className="col-xs-12">
              <PhoneInput
                initialValue={user.mobile}
                updatedValue={user.mobile}
                onChange={mobile => (this.model = { ...this.model, mobile })}
                forwardValidator={validate => (this.validateMobile = validate)}
                error={this.state.errors.mobile}
              />
            </div>

            {!user.objectId && (
              <div className="col-xs-12">
                <FormField
                  id="user-password"
                  name="password"
                  labelText="Password (min. 6 characters)"
                  inputType="password"
                  autocomplete="new-password"
                  onChange={password =>
                    (this.model = { ...this.model, password })
                  }
                  validate={password => this.validatePassword(password)}
                  error={this.state.errors.password}
                />
              </div>
            )}

            {showPromo && (
              <div className="col-xs-12">
                <FormField
                  id="user-promo"
                  name="promo"
                  labelText="Promo Code or Gift Card (optional)"
                  initialValue={this.props.promo}
                  updatedValue={this.props.promo}
                  onChange={this.props.onPromoChange}
                />
              </div>
            )}

            <div className="col-xs-12">
              {this.state.sourceOptions && (
                <Select
                  instanceId="user-source"
                  id="user-source"
                  name="source"
                  placeholder="How did you hear about Saucey? (optional)"
                  value={this.state.source}
                  options={this.state.sourceOptions}
                  onChange={source => this.setState({ source })}
                  scrollMenuIntoView={false}
                  searchable={false}
                  onSuggestionsFetchRequested={this.noop}
                  onSuggestionsClearRequested={this.noop}
                />
              )}
            </div>
          </div>

          <div className="row center-xs start-sm bottom-md">
            <div className="col-xs-12 col-sm-6 col-md-8">
              <div className={`${margin.bottom_sm} ${margin.tablet_bottom_0}`}>
                <small>
                  <span>By continuing you agree to the </span>
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    className="link-text"
                  >
                    Terms of Service
                  </a>
                  <span> & </span>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    className="link-text"
                  >
                    Privacy Policy
                  </a>
                </small>
              </div>
            </div>

            <div className={`col-xs-12 col-sm-6 col-md-4 ${tablet_textRight}`}>
              <LoadingButton
                type="submit"
                disabled={this.state.loading || this.state.fbLoading}
                loading={this.state.loading || this.props.loading}
              >
                Continue
              </LoadingButton>
            </div>
          </div>
        </form>
      </div>
    );
  }
};

SignupForm = connect(
  state => ({
    user: state.login.user || {}
  }),
  { signup, updateUser }
)(SignupForm);

export default SignupForm;
